import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheckCircle, FiAlertTriangle, FiMessageSquare, FiInfo, FiChevronDown, FiX, FiLink, FiCheck, FiClipboard, FiFileText, FiMove } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

/*
 * NOTIFICATION MODAL - UPDATED FOR NEW DARK THEME
 * 
 * This component has been updated to use the new dark theme colors:
 * - Modal background: #1F2937 (gray-800) instead of #1a1a1a
 * - Text colors: #F9FAFB (gray-50) instead of #E0E0E0  
 * - Maintains consistency with the rest of the application
 * 
 * The colors are hardcoded here for performance reasons (inline styles)
 * but match the CSS custom properties defined in theme.css
 */

// Helper function to format notification message with bullets
const formatMessageWithBullets = (message, isDarkMode) => {
  if (!message) return '';
  
  const lines = message.split('\n');
  
  /* 
   * NOTIFICATION TEXT COLORS
   * Light theme: #1F2937 (gray-800) - dark text for good contrast on light backgrounds
   * Dark theme: #F9FAFB (gray-50) - very light text for good contrast on dark backgrounds
   * These match the new dark theme color palette
   */
  const textColor = isDarkMode ? '#F9FAFB' : '#1F2937';
  
  const textStyle = {
    color: `${textColor} !important`,
    background: 'none !important' // Ensure no background on the span itself
    // Rely on global index.css for text-fill and background-clip resets
  };
  
  return lines.map((line, index) => {
    const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);
    
    if (bulletMatch) {
      return (
        <React.Fragment key={index}>
          <div className="flex items-start">
            {/* Use the --bullet-color CSS variable instead of Tailwind classes */}
            <span 
              className="mr-2 mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full" 
              style={{ backgroundColor: 'var(--bullet-color)' }}
            ></span>
            <span style={textStyle}>{bulletMatch[1]}</span>
          </div>
          {index < lines.length - 1 && ''}
        </React.Fragment>
      );
    } else if (line.trim()) {
      return (
        <React.Fragment key={index}>
          <span style={textStyle}>{line}</span>
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      );
    } else {
      return <br key={index} />;
    }
  });
};

const NotificationModal = ({ isOpen, notifications, onClose, onMarkAsRead, anchorPosition = 'desktop', style = {} }) => {
  const INITIAL_DISPLAY_COUNT = 3;
  const LOAD_MORE_COUNT = 10;
  const POSITION_STORAGE_KEY = 'notification-modal-position';

  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const modalRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Handler functions need to be declared before hooks that use them
  const handleMouseMove = (e) => {
    if (isDragging && modalRef.current) {
      const newX = e.clientX - dragStartRef.current.x;
      const newY = e.clientY - dragStartRef.current.y;
      
      // Constrain to viewport (with some margin)
      const maxX = window.innerWidth - modalRef.current.offsetWidth;
      const maxY = window.innerHeight - modalRef.current.offsetHeight;
      
      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY)
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header-draggable') && modalRef.current) {
      // Get the current modal's position from the DOM
      const rect = modalRef.current.getBoundingClientRect();
      
      // Calculate the offset between mouse position and modal's top-left corner
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      
      // Store these offsets to use during drag
      dragStartRef.current = { x: offsetX, y: offsetY };
      
      // If the modal doesn't have a custom position yet, set initial position
      if (position.x === null || position.y === null) {
        setPosition({
          x: rect.left,
          y: rect.top
        });
      }
      
      setIsDragging(true);
      e.preventDefault();
    }
  };

  // Load saved position on initial render
  useEffect(() => {
    try {
      const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
      if (savedPosition) {
        const parsedPosition = JSON.parse(savedPosition);
        // Verify the saved position is still within the current viewport
        if (parsedPosition.x < window.innerWidth && parsedPosition.y < window.innerHeight) {
          setPosition(parsedPosition);
        }
      }
    } catch (e) {
      console.error("Error loading saved position:", e);
    }
  }, []);

  // Save position when modal closes and position is defined
  useEffect(() => {
    if (!isOpen && position.x !== null && position.y !== null) {
      try {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
      } catch (e) {
        console.error("Error saving position:", e);
      }
    }
  }, [isOpen, position]);

  // All useEffect hooks must be placed before any conditional returns
  useEffect(() => {
    if (isOpen) {
      setDisplayCount(INITIAL_DISPLAY_COUNT);
      
      // Load the saved position if available, otherwise reset it
      try {
        const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
        if (savedPosition) {
          const parsedPosition = JSON.parse(savedPosition);
          // Verify the saved position is still within the current viewport
          if (parsedPosition.x < window.innerWidth && parsedPosition.y < window.innerHeight) {
            setPosition(parsedPosition);
          } else {
            setPosition({ x: null, y: null }); // Reset if outside viewport
          }
        } else {
          setPosition({ x: null, y: null });
        }
      } catch (e) {
        console.error("Error loading saved position:", e);
        setPosition({ x: null, y: null });
      }
    }
  }, [isOpen, notifications.length]);

  useEffect(() => {
    if (isOpen) {
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Add drag handling effect before the conditional return
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  const notificationsToDisplay = notifications.slice(0, displayCount);
  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationStyles = (notification) => {
    const type = notification.type || 'general';
    let icon, iconClass, bgClass;

    if (type.includes('osr') || notification.priority === 'high') {
      icon = <FiAlertTriangle className="w-4 h-4" />;
      iconClass = 'text-red-600 dark:text-red-400';
      bgClass = 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30';
    } else if (type.includes('changelog')) {
      icon = <FiFileText className="w-4 h-4" />;
      iconClass = 'text-amber-600 dark:text-amber-400';
      bgClass = 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/30';
    } else if (type.includes('broadcast')) {
      icon = <FiMessageSquare className="w-4 h-4" />;
      iconClass = 'text-blue-600 dark:text-blue-400';
      bgClass = 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30';
    } else if (type.includes('eowr')) {
      icon = <FiInfo className="w-4 h-4" />;
      iconClass = 'text-purple-600 dark:text-purple-400';
      bgClass = 'bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/30';
    } else if (type.includes('dor')) {
      icon = <FiCheckCircle className="w-4 h-4" />;
      iconClass = 'text-green-600 dark:text-green-400';
      bgClass = 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/30';
    } else {
      icon = <FiBell className="w-4 h-4" />;
      iconClass = 'text-accent-light dark:text-accent-dark';
      bgClass = 'bg-accent-light/5 dark:bg-accent-dark/10 border-accent-light/10 dark:border-accent-dark/20';
    }
    
    return { icon, iconClass, bgClass };
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Define the position and animation classes based on anchorPosition
  let modalContainerClasses = "fixed z-[1000] inset-0 bg-black/10 dark:bg-black/20 backdrop-blur-[2px] flex items-center justify-center";
  let modalAnimationClass = anchorPosition === 'desktop' 
    ? "animate-fadeScale" 
    : "animate-slideUp";
  
  let modalPositionClass = "";
  if (anchorPosition === 'desktop') {
    modalPositionClass = "absolute top-16 right-6 sm:right-8 w-[calc(100vw-3rem)] sm:w-96 md:w-[420px] lg:w-[450px] max-w-md";
  } else {
    modalPositionClass = "absolute bottom-24 right-4 left-4 sm:left-auto sm:w-80";
  }

  // Apply custom positioning if the user has dragged the modal
  const modalStyle = {
    ...style,
    ...(position.x !== null && position.y !== null ? {
      position: 'fixed',
      top: `${position.y}px`,
      left: `${position.x}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none'
    } : {})
  };

  const cursorClass = isDragging ? 'cursor-grabbing' : 'cursor-grab';

  return (
    <div 
      className={`${modalContainerClasses} ${isOpen ? 'animate-fadeIn' : 'animate-fadeOut'}`}
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div
        ref={modalRef}
        className={`${modalPositionClass} ${modalAnimationClass} rounded-xl overflow-hidden flex flex-col shadow-xl border border-secondary-light dark:border-secondary-dark`}
        role="dialog"
        aria-labelledby="notification-modal-title"
        style={{
          ...modalStyle,
          /* 
           * MODAL BACKGROUND COLORS
           * Light theme: #ffffff (white) - clean, bright background
           * Dark theme: #1F2937 (gray-800) - matches our new dark theme primary color
           * This ensures consistency with cards and panels throughout the app
           */
          backgroundColor: isDarkMode ? '#1F2937' : '#ffffff',
          opacity: 1
        }}
      >
        {/* Header - Now draggable */}
        <div 
          className={`flex items-center justify-between p-4 border-b border-secondary-light/50 dark:border-secondary-dark/50 modal-header-draggable ${cursorClass}`}
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <div className="relative">
              <FiBell className="w-5 h-5 text-accent-light dark:text-accent-dark" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-primary-light dark:border-primary-dark"></span>
              )}
            </div>
            <h3 id="notification-modal-title" className="text-base font-semibold text-text-light dark:text-text-dark">
              Notifications {unreadCount > 0 && <span className="text-xs ml-1.5 font-normal opacity-70">({unreadCount} unread)</span>}
            </h3>
          </div>
          <div className="flex items-center">
            <div className="mr-2 text-text-light/50 dark:text-text-dark/50" title="Drag to move">
              <FiMove className="w-4 h-4" />
          </div>
          <button 
            type="button" 
            className="p-1.5 rounded-full hover:bg-secondary-light dark:hover:bg-secondary-dark transition-colors text-text-light/70 dark:text-text-dark/70 hover:text-text-light dark:hover:text-text-dark" 
            aria-label="Close" 
            onClick={onClose}
          >
            <FiX className="w-5 h-5" />
          </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-96 overflow-y-auto flex-grow p-2">
          {notifications.length === 0 ? (
            <div className="py-10 flex flex-col items-center justify-center text-center text-text-light/60 dark:text-text-dark/60">
              <FiBell className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs mt-1">You'll see important updates here</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {notificationsToDisplay.map((notif, idx) => {
                const { icon, iconClass, bgClass } = getNotificationStyles(notif);
                
                const isUnread = !notif.read;
                
                const itemClasses = `p-3 rounded-lg border transition-all duration-200 flex items-start gap-3 relative
                  ${isUnread ? `${bgClass}` : 'bg-secondary-light/30 dark:bg-secondary-dark/30 border-secondary-light/40 dark:border-secondary-dark/40'}
                  ${notif.linkTo ? 'cursor-pointer hover:shadow-md transform hover:-translate-y-0.5' : ''}
                  ${isUnread ? 'shadow-sm' : 'opacity-80 hover:opacity-100'}`;

                const handleNotificationClick = () => {
                  if (isUnread) {
                    onMarkAsRead(notif);
                  }
                  if (notif.linkTo) {
                    navigate(notif.linkTo);
                    onClose(); 
                  }
                };

                return (
                  <li
                    key={notif.id || idx}
                    className={itemClasses}
                    onClick={notif.linkTo ? handleNotificationClick : undefined}
                    role={notif.linkTo ? "button" : undefined}
                    tabIndex={notif.linkTo ? 0 : undefined}
                    onKeyDown={(e) => notif.linkTo && (e.key === 'Enter' || e.key === ' ') && handleNotificationClick()}
                  >
                    {/* Icon */}
                    <div className={`mt-0.5 ${iconClass} flex-shrink-0`}>
                      {icon}
                    </div>

                    {/* Content */}
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm text-text-light dark:text-text-dark">
                          {notif.title || 'Notification'}
                        </span>
                        
                        {/* Right side actions or indicators */}
                        <div className="flex items-center gap-1.5 ml-2">
                          {notif.linkTo && (
                            <FiLink className="w-3 h-3 text-accent-light/70 dark:text-accent-dark/70" title="Links to page" />
                          )}
                          {isUnread && (
                            <div className="w-2 h-2 bg-accent-light dark:bg-accent-dark rounded-full" title="Unread"></div>
                          )}
                        </div>
                      </div>

                      {/* Message - Now with bullet point formatting */}
                      <div 
                        className="text-xs whitespace-pre-wrap break-words" 
                        style={{
                          // Ensures notification message text uses consistent theme colors
                          color: `${isDarkMode ? '#F9FAFB' : '#1F2937'} !important`, // gray-50/gray-800
                          background: 'none !important'
                        }}
                      >
                        {formatMessageWithBullets(notif.message, isDarkMode)}
                      </div>

                      {/* Footer: Timestamp, sender, etc. */}
                      {(notif.timestamp || notif.senderName) && (
                        <div className="flex justify-between items-center mt-2 text-text-light/50 dark:text-text-dark/50">
                          {notif.timestamp && (
                            <div className="text-[10px]">
                              {new Date(notif.timestamp.seconds ? notif.timestamp.seconds * 1000 : notif.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                          )}
                          {notif.senderName && (
                            <div className="text-[10px] font-medium text-accent-light dark:text-accent-dark truncate ml-auto pl-2" title={notif.senderName}>
                              {notif.senderName}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Mark as read button (only show for unread notifications) */}
                    {isUnread && !notif.linkTo && (
                      <button 
                        className="p-1.5 rounded-full hover:bg-secondary-light dark:hover:bg-secondary-dark text-text-light/50 dark:text-text-dark/50 hover:text-accent-light dark:hover:text-accent-dark flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(notif);
                        }}
                        aria-label="Mark as read"
                        title="Mark as read"
                      >
                        <FiCheck className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div> 

        {/* Footer Actions */}
        {(unreadCount > 0 || notifications.length > displayCount) && (
          <div className="p-3 border-t border-secondary-light/50 dark:border-secondary-dark/50 flex flex-col sm:flex-row justify-center items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={() => {
                  notifications.filter(n => !n.read).forEach(onMarkAsRead);
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-md w-full sm:w-auto 
                           bg-accent-light hover:brightness-110 dark:bg-accent-dark dark:hover:brightness-110 
                           text-white transition-all duration-200 shadow-sm"
              >
                <FiCheck className="inline mr-1.5 w-3 h-3" />
                Mark All as Read
              </button>
            )}
            
            {notifications.length > displayCount && (
              <button
                onClick={() => setDisplayCount(prev => prev + LOAD_MORE_COUNT)}
                className="px-3 py-1.5 text-xs font-medium rounded-md w-full sm:w-auto
                           bg-secondary-light hover:bg-secondary-light/70 
                           dark:bg-secondary-dark dark:hover:bg-secondary-dark/70 
                           text-text-light dark:text-text-dark
                           transition-all duration-200 shadow-sm"
              >
                <FiChevronDown className="inline mr-1.5 w-3 h-3" /> 
                Load More ({notifications.length - displayCount})
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;